import {
  CrudFilter,
  CrudSort,
  useNotification,
  useResourceParams,
} from "@refinedev/core";
import { UseTableReturnType } from "@refinedev/react-table";
import { TableState } from "@tanstack/react-table";
import _ from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router";

export interface IView {
  id: string;
  name: string;
  tableState: TableState;
  refineState: {
    filters: CrudFilter[];
    sorters: CrudSort[];
    pageSize: number;
  };
  uri?: string;
}

export const useStatePersistence = ({
  resource: resourceFromProps,
  table,
}: {
  resource: string;
  table: UseTableReturnType<any>;
}) => {
  const [views, setViews] = useState<IView[]>([]);
  const [currentView, setCurrentView] = useState<IView>();
  const { open } = useNotification();

  const { resource, identifier } = useResourceParams({
    resource: resourceFromProps,
  });
  const location = useLocation();

  const resourceName = identifier ?? resource?.name;
  const storageKey = `${resourceName}-list-views`;

  useEffect(() => {
    console.debug("Fetching saved views...");
    const views = getSavedViews();

    const sameAs = views.find(
      (v) => v.uri === location.pathname + location.search + location.hash,
    );
    console.debug(sameAs);
    if (sameAs) {
      selectView(sameAs.id, sameAs);
      open?.({
        message: `Selected view ${sameAs.name}`,
        description: "This view was selected by the url query params",
        type: "success",
      });
    }
  }, []);

  const addView = ({ name }: { name: string }) => {
    const view: IView = {
      id: crypto.randomUUID(),
      name,
      refineState: {
        filters: table.refineCore.filters,
        sorters: table.refineCore.sorters,
        pageSize: table.refineCore.pageSize,
      },
      tableState: table.reactTable.getState(),
      uri: location.pathname + location.search + location.hash,
    };

    const allViews: IView[] = JSON.parse(
      localStorage.getItem(storageKey) ?? "[]",
    );

    allViews.unshift(view);

    localStorage.setItem(storageKey, JSON.stringify(allViews));
    setViews(allViews);
  };

  const deleteView = (id: string) => {
    const newViews = views.filter((v) => v.id !== id);
    setViews(newViews);
    localStorage.setItem(storageKey, JSON.stringify(newViews));
  };

  const getSavedViews = (): IView[] => {
    const stringifiedViews = localStorage.getItem(storageKey);
    const views = JSON.parse(stringifiedViews ?? "[]");
    setViews(views);
    return views;
  };

  const selectView = useCallback(
    (id: string, view?: IView) => {
      if (currentView?.id === id) {
        setCurrentView(undefined);
        table.reactTable.reset();
        table.refineCore.setFilters([], "replace");
        table.refineCore.setSorters([]);
        return;
      }

      const foundView: IView | undefined =
        view || views.find((v) => v.id === id);

      if (foundView) {
        setCurrentView(foundView);
        table.refineCore.setFilters(foundView.refineState.filters, "replace");
        table.refineCore.setSorters(foundView.refineState.sorters);
        table.refineCore.setPageSize(foundView.refineState.pageSize);
        table.reactTable.setState(foundView.tableState);
      } else {
        console.warn("NO SUCH VIEW WITH ID", id);
      }
    },
    [views, currentView],
  );

  return {
    views,
    addView,
    deleteView,
    selectView,
    currentView,
  };
};
